import { useDependency } from 'ditox-react';
import { ArrowLeft2, Gallery } from 'iconsax-react';
import { useSubscription } from 'observable-hooks';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { parseURL } from 'ufo';
import { useEventListener, useToggle } from 'usehooks-ts';
import QrCodeImage from '@/assets/images/icons/qr-code.svg?react';
import MyCodeFlyout from '@/components/scan-code/my-code-flyout';
import { CODE_SCANNER_TOKEN } from '@/container';
import { useScreenOrientation } from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import copyTextToClipboard from '@/utils/copy-text';

export default function ScanCode() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const codeScanner = useDependency(CODE_SCANNER_TOKEN);
  const documentRef = useRef<Document>(document);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMyCodeOpen, , setMyCodeOpen] = useToggle(false);
  const [scanResult, setScanResult] = useState<string | null>(null);
  const orientation = useScreenOrientation();

  useEventListener(
    'visibilitychange',
    () => {
      if (documentRef.current.visibilityState === 'visible') {
        (async () => {
          if (videoRef.current) {
            await codeScanner.startScanner(videoRef.current);
          }
        })();
      } else {
        codeScanner.stopScanner();
      }
    },
    documentRef,
  );

  useEffect(() => {
    (async () => {
      if (videoRef.current) {
        await codeScanner.startScanner(videoRef.current);
      }
    })();

    return () => {
      codeScanner.stopScanner();
    };
  }, [codeScanner]);

  useSubscription(codeScanner.event$, (event) => {
    const text = event.data;
    const url = parseURL(text);

    const searchParams = new URLSearchParams(url.search);
    let codeType = searchParams.get('qrcodetype');

    if (url.pathname.includes('/registration/')) {
      codeType = 'registration';
    }

    switch (codeType?.toLowerCase()) {
      case 'g':
      case '66chatgroup':
        navigate(ROUTES.CONTACT.REQUEST.GROUP.$buildPath({ searchParams: { code: text } }));
        break;
      case 'u':
      case '66':
        navigate(ROUTES.CONTACT.REQUEST.USER.$buildPath({ searchParams: { code: text, source: 'qrcode' } }));
        break;
      case 'registration':
        navigate(ROUTES.CONTACT.REQUEST.INVITE.$buildPath({ searchParams: { code: url.pathname.split('/').at(-1) } }));
        break;
      default:
        setScanResult(text);
        break;
    }
  });

  const handleScanFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0];

    if (!file) throw 'INVALID_FILE';

    return codeScanner.scanFile(file);
  };

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className='relative flex w-screen flex-1 flex-col landscape:flex-row items-stretch justify-center h-screen-safe'>
      <div className='absolute inset-0'>
        <div
          style={{
            width: '100%',
            height: '100%',
          }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </div>
      </div>

      {orientation === 'landscape' && (
        <div className='absolute inset-0 bg-cover z-40 bg-center backdrop-blur-lg bg-neutral-950/25 flex flex-center text-neutral-0'>
          {t('contact.scan-code.incorrect-orientation')}
        </div>
      )}

      <button
        type='button'
        className='btn btn-square btn-ghost absolute left-safe-offset-4 top-safe-offset-4 z-50 hover:shadow'
        onClick={() => navigate(-1)}>
        <ArrowLeft2
          size={24}
          variant='Outline'
          className='aspect-square size-6 text-slate-200'
        />
      </button>

      <div className='m-auto size-70dvw landscape:size-50dvh relative z-10 aspect-square rounded-3xl border-4 border-slate-200 bg-transparent shadow-[0_0_1px_100vw_rgba(0,0,0,.5)]'>
        <p className='absolute inset-x-0 top-full translate-y-8 text-center align-middle text-slate-200'>
          {t('contact.scan-code.alert')}
        </p>

        {scanResult && (
          <div className='absolute inset-x-0 top-full translate-y-1/4 flex z-30 w-full items-center gap-2 rounded bg-neutral-0 p-2'>
            <code className='flex-1 truncate p-2'>{scanResult}</code>

            <button
              type='button'
              className='btn btn-primary'
              onClick={() => copyTextToClipboard(scanResult)}>
              {t('common.copy')}
            </button>
          </div>
        )}
      </div>

      <div className='z-20 flex flex-col gap-8 landscape:pe-safe-offset-8 portrait:pb-safe-offset-8'>
        <div className='flex landscape:flex-col landscape:flex-1 justify-evenly text-slate-200'>
          <button
            type='button'
            className='flex flex-col items-center gap-2'
            onClick={() => setMyCodeOpen(true)}>
            <QrCodeImage className='size-6' />

            <span className='text-body1'>{t('contact.scan-code.my-code')}</span>
          </button>

          <button
            type='button'
            className='flex flex-col items-center gap-2'
            onClick={handlePickFile}>
            <Gallery
              variant='Bold'
              size={24}
              className='size-6'
            />

            <span className='text-body1'>{t('upload.album')}</span>
          </button>
        </div>

        <input
          type='file'
          ref={fileInputRef}
          className='hidden'
          accept='image/*'
          onChange={handleScanFile}
        />
      </div>

      <MyCodeFlyout
        open={isMyCodeOpen}
        onClose={() => setMyCodeOpen(false)}
      />
    </div>
  );
}
